import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { AppConfigurationService } from '@app-config/services/app-configuration.service';
import { Credential } from '@auth/models/credential';
import { serverMessagesMapping } from '@auth/pages/login/server-message-mapping/server-message-mapping';
import * as loginActions from '@auth/store/actions/login.actions';
import { AuthEffects } from '@auth/store/effects/auth.effects';
import { UserStoreService } from '@auth/store/services/user-store.service';
import { fadeInScaleAnimation } from '@core-animations/element-animations';
import { RpcInputOptions } from '@core-controls/components/rpc-input/models/rpc-input-options';
import { RpcRoute } from '@core-layout/app/models/rpc-route';
import { RouteService } from '@core-layout/app/services/route.service';
import { ServerMessageService } from '@core-services/server-message.service';
import { ToastService } from '@core-services/toast.service';
import { ProgressBarService } from '@core-utils/progress-bar/progress-bar.service';
import { ApiError } from '@error/models/api-error';
import { PRIVACY_POLICY_LINK, TERMS_AND_CONDITIONS_LINK } from '@auth/constants/terms-and-conditions';
import { LogoComponent } from '../../../../core-modules/layout/logo/logo.component';
import { RpcInputComponent } from '../../../../core-modules/controls/components/rpc-input/rpc-input.component';
import { PasswordComponent } from '../../../../core-modules/controls/components/password/password.component';
import { RouterLink } from '@angular/router';
import { RpcButtonComponent } from '../../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { AdditionalFooterComponent } from '../../../../core-modules/layout/additional-footer/additional-footer.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'login',
    templateUrl: './login.component.html',
    styleUrls: ['../../auth-base/auth-base.component.scss', './login.component.scss'],
    animations: [fadeInScaleAnimation],
    imports: [LogoComponent, FormsModule, ReactiveFormsModule, RpcInputComponent, PasswordComponent, RouterLink, RpcButtonComponent, AdditionalFooterComponent, TranslateModule]
})
export class LoginComponent implements OnInit, OnDestroy {

    public readonly RpcRoute = RpcRoute;

    public loginForm: UntypedFormGroup;
    public userNameControlOptions: RpcInputOptions;
    public passwordControlOptions: RpcInputOptions;
    public readonly companyConfiguration$ = this.configurationService.configuration$.pipe(map(configuration => configuration.company));
    public termsAndConditionsLink = TERMS_AND_CONDITIONS_LINK;
    public privacyPolicyLink = PRIVACY_POLICY_LINK;
    public readonly companyLogo$ = this.configurationService.getCompanyLogo();

    private readonly unsubscribe$ = new Subject<void>();

    constructor(
        private readonly configurationService: AppConfigurationService,
        private readonly authEffects: AuthEffects,
        private readonly routeService: RouteService,
        private readonly toaster: ToastService,
        private readonly progressBarService: ProgressBarService,
        private readonly serverMessageService: ServerMessageService,
        private readonly userStoreService: UserStoreService,
        private readonly renderer: Renderer2,
    ) {
        configurationService.configuration = { layout: { spinner: { hidden: true } } };

        this.serverMessageService.loadServerMessagesMapping(serverMessagesMapping);
    }

    public ngOnInit(): void {
        this.renderer.addClass(document.body, 'auth-page');

        this.buildForm();

        this.authEffects.tryLogin$
            .pipe(ofType(loginActions.loginWithinAgentsSelection), takeUntil(this.unsubscribe$))
            .subscribe(
                (loginResponse) => {
                    this.userStoreService.setLoginProcessData(loginResponse.credentials);
                    this.routeService.navigate(RpcRoute.AgentSelection, { queryParamsHandling: 'preserve' }).finally(() => this.progressBarService.hide());
                },
                (error: ApiError) => this.showErrorMessage(error)
            );

        this.authEffects.tryLogin$
            .pipe(ofType(loginActions.loginFailed), takeUntil(this.unsubscribe$))
            .subscribe((error: ApiError) => this.showErrorMessage(error));
    }

    public ngOnDestroy(): void {
        this.renderer.removeClass(document.body, 'auth-page');

        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public login(): void {
        this.userStoreService.tryLogin(this.loginForm.getRawValue() as Credential);
        this.progressBarService.show();
    }

    private buildForm(): void {
        this.loginForm = new UntypedFormGroup({});

        this.userNameControlOptions = {
            formGroup: this.loginForm,
            controlName: 'userName',
            validatorsMap: [
                {
                    message: 'LOGIN.VALIDATION.USERNAME_REQUIRED_VALIDATION',
                    showError: (control: UntypedFormControl) => control.hasError('required'),
                    validator: Validators.required
                }
            ]
        };

        this.passwordControlOptions = {
            formGroup: this.loginForm,
            controlName: 'password',
            validatorsMap: [
                {
                    message: 'LOGIN.VALIDATION.PASSWORD_REQUIRED_VALIDATION',
                    showError: (control: UntypedFormControl) => control.hasError('required'),
                    validator: Validators.required
                }
            ]
        };
    }

    private showErrorMessage(error: ApiError): void {
        this.toaster.showServerError(error);
        this.progressBarService.hide();
    }
}